// @ts-ignore (no typings)
import * as yaml from 'js-yaml';
import * as zod from 'zod';

import * as log from '@sb/log';
import { DEFAULT_JOINT_DEVIATION_THRESHOLDS_RADIANS } from '@sb/routine-runner/configuration/defaults';

import { loadFS } from './fs';

const ns = log.namespace('routineRunnerConfig');

const finiteNumber = zod.coerce
  .number()
  .refine((v) => Number.isFinite(v), 'Must be a finite number');

export const RoutineRunnerConfigZod = zod.object({
  // Six-length array of per-joint thresholds in radians
  jointDeviationThresholdsRadians: zod
    .tuple([
      finiteNumber,
      finiteNumber,
      finiteNumber,
      finiteNumber,
      finiteNumber,
      finiteNumber,
    ])
    .optional(),
});

export type RoutineRunnerConfig = zod.infer<typeof RoutineRunnerConfigZod>;

const ETC_CONFIG_PATH = '/etc/standardbots/configuration/routine_runner.yaml';

let cache: RoutineRunnerConfig | undefined;

export const loadRoutineRunnerConfig = async (
  refreshCache = false,
): Promise<RoutineRunnerConfig> => {
  if (cache && !refreshCache) return cache;

  try {
    const fs = await loadFS();

    let thresholds = DEFAULT_JOINT_DEVIATION_THRESHOLDS_RADIANS as number[];

    // Attempt to load from /etc path
    try {
      log.info(ns`load`, 'Reading routine runner config from /etc', {
        path: ETC_CONFIG_PATH,
      });

      const raw = await fs.readFile(ETC_CONFIG_PATH, 'utf-8');
      const doc = (yaml.load(raw) || {}) as unknown;

      const parsed = RoutineRunnerConfigZod.safeParse(doc);

      if (parsed.success) {
        const t = parsed.data.jointDeviationThresholdsRadians;

        if (t) {
          thresholds = [...t];

          log.info(ns`load`, 'Loaded routine runner config from /etc', {
            path: ETC_CONFIG_PATH,
          });
        }
      } else {
        log.warn(ns`load`, 'Invalid routine runner config in /etc', {
          path: ETC_CONFIG_PATH,
          issues: parsed.error.issues.map((i) => i.message).join('; '),
        });
      }
    } catch (err: any) {
      log.warn(
        ns`load`,
        'Failed to read /etc routine runner config, using defaults',
        {
          path: ETC_CONFIG_PATH,
          message: err?.message,
        },
      );
    }

    cache = { jointDeviationThresholdsRadians: thresholds as any };

    return cache;
  } catch (e: any) {
    log.warn(
      ns`load`,
      'Failed to load routine runner YAML config. Using defaults.',
      {
        path: ETC_CONFIG_PATH,
        message: e?.message,
      },
    );

    cache = {
      jointDeviationThresholdsRadians:
        DEFAULT_JOINT_DEVIATION_THRESHOLDS_RADIANS as any,
    };

    return cache;
  }
};

export const getRoutineRunnerConfigSync = (): RoutineRunnerConfig | undefined =>
  cache;
